import { Box, Button, Card, Chip, Container, Divider, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import { DateTime } from 'luxon';
import { CustomerRental, RentalWithTrailer } from 'src/models';
import { Constants, isRentalTruckType, rentalTrailerType } from 'src/constants';
import useModelData from 'src/hooks/use-model-data';
import { DisplayPageData } from 'src/components/display-page-data';
import NoneFound from 'src/components/none-found';
import PageHeader from 'src/components/page-header';
import Iconify from 'src/components/iconify';
import rentalsCustomerAxios, { rentalsCustomerEndpoints } from 'src/utils/rentals-customer-axios';
import { useRentalsCustomerAuth } from 'src/auth/rentals-customer/rentals-customer-auth';

type PageModel = {
  rentalData: {
    rentals: RentalWithTrailer[];
    customerRentals: CustomerRental[];
  };
};

// before the customer signs, the sent (unsigned) agreement is what there is to show
export function rentalAgreementUrl(rental: RentalWithTrailer) {
  return rental.event.signedContractPdfUrl || rental.event.appliedContractInfo?.contractPdfUrl || '';
}

export function RentalStatusChip({ rental }: { rental: RentalWithTrailer }) {
  if (rental.event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_IN) {
    return <Chip size="small" color="info" label="Returned - Being Processed" />;
  }
  const status = rental.event.rentalTrailerEventStatus;
  if (status === Constants.RENTAL_TRAILER_EVENT_STATUS_AWAITING_SIGNATURE) {
    return <Chip size="small" color="primary" label="Waiting for your Signature" />;
  }
  if (status === Constants.RENTAL_TRAILER_EVENT_STATUS_AWAITING_CONFIRMATION) {
    return <Chip size="small" color="info" label="Waiting for Confirmation" />;
  }
  if (status === Constants.RENTAL_TRAILER_EVENT_STATUS_SIGNED) {
    return <Chip size="small" color="success" label="Active" />;
  }
  return <Chip size="small" label="Being Prepared" />;
}

// one entry per lifecycle stage, worded for the customer
const CUSTOMER_STAGE_DISPLAY: Record<number, { label: string; color: 'default' | 'primary' | 'info' | 'success' }> = {
  [Constants.RENTAL_STATUS_DRAFT]: { label: 'Being Prepared', color: 'default' },
  [Constants.RENTAL_STATUS_INSURANCE_SENT]: { label: 'Insurance Forms Needed', color: 'primary' },
  [Constants.RENTAL_STATUS_INSURANCE_SUBMITTED]: { label: 'Insurance Under Review', color: 'info' },
  [Constants.RENTAL_STATUS_INSURANCE_CONFIRMED]: { label: 'Insurance Confirmed', color: 'info' },
  [Constants.RENTAL_STATUS_AGREEMENT_SENT]: { label: 'Waiting for your Signature', color: 'primary' },
  [Constants.RENTAL_STATUS_AGREEMENT_SUBMITTED]: { label: 'Agreement Under Review', color: 'info' },
  [Constants.RENTAL_STATUS_AGREEMENT_CONFIRMED]: { label: 'Preparing for Pickup', color: 'info' },
  [Constants.RENTAL_STATUS_ACTIVE]: { label: 'Active', color: 'success' },
  [Constants.RENTAL_STATUS_RETURNED]: { label: 'Returned - Being Processed', color: 'info' },
  [Constants.RENTAL_STATUS_COMPLETED]: { label: 'Completed', color: 'default' },
};

export function CustomerRentalStatusChip({ rental, outboundEvent }: { rental: CustomerRental; outboundEvent?: RentalWithTrailer }) {
  // the outbound form sent for remote signing happens while the rental is
  // still Preparing for Pickup; that signature is the clearer status
  if (
    rental.rentalStatus === Constants.RENTAL_STATUS_AGREEMENT_CONFIRMED &&
    outboundEvent?.event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_AWAITING_SIGNATURE
  ) {
    return <Chip size="small" color="primary" label="Waiting for your Signature" />;
  }
  const display = CUSTOMER_STAGE_DISPLAY[rental.rentalStatus];
  return <Chip size="small" color={display?.color || 'default'} label={display?.label || 'Being Prepared'} />;
}

export function DocumentButton({ label, url }: { label: string; url: string }) {
  if (!url) {
    return null;
  }
  return (
    <Button
      size="small"
      variant="soft"
      href={url}
      target="_blank"
      rel="noopener"
      startIcon={<Iconify icon="solar:document-text-bold" width={16} />}
    >
      {label}
    </Button>
  );
}

function rentalAction(rental: CustomerRental, outboundEvent?: RentalWithTrailer) {
  if (rental.insuranceUuid) {
    return { label: 'Fill Insurance Forms', href: `/insurance-request/${rental.insuranceUuid}` };
  }
  if (rental.agreementUuid) {
    return { label: 'Sign Agreement', href: `/sign-rental-agreement/${rental.agreementUuid}` };
  }
  if (
    outboundEvent?.event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_AWAITING_SIGNATURE &&
    outboundEvent.event.signatureUuid
  ) {
    return { label: 'Sign Outbound Form', href: `/sign-rental-contract/${outboundEvent.event.signatureUuid}` };
  }
  return null;
}

export default function Page() {
  const { session } = useRentalsCustomerAuth();
  const timezone = session?.organizationSettings?.companyTimeZone || 'America/Chicago';

  const rentalPeriod = (rental: CustomerRental) => {
    const format = (iso: string) => DateTime.fromISO(iso).setZone(timezone).toFormat('MM/dd/yyyy');
    if (!rental.rentalStartTime) {
      return '';
    }
    return `${format(rental.rentalStartTime)} - ${
      rental.rentalEndTime ? format(rental.rentalEndTime) : 'Open'
    }`;
  };

  const dataLoader = useCallback(
    () => ({
      rentalData: rentalsCustomerAxios
        .get(rentalsCustomerEndpoints.rentalData)
        .then((response) => response.data),
    }),
    []
  );

  const { data, error, loading } = useModelData<PageModel>(dataLoader);

  const customerRentals = data?.rentalData.customerRentals || [];
  const events = data?.rentalData.rentals || [];
  const outboundByRentalId = new Map(
    events
      .filter((entry) => entry.event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT)
      .map((entry) => [entry.event.rentalId, entry])
  );
  // rentals from before the staged flow have no lifecycle row, only an event
  const legacyEvents = events.filter((entry) => !entry.event.rentalId);

  return (
    <Container maxWidth="lg">
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && (
        <>
          <PageHeader title="My Rentals" />

          {!customerRentals.length && !legacyEvents.length && (
            <NoneFound
              title="No Rentals"
              message="Once a rental is started for you it will show up here"
            />
          )}

          <Stack gap={2}>
            {customerRentals.map((rental) => {
              const outboundEvent = outboundByRentalId.get(rental.id);
              const action = rentalAction(rental, outboundEvent);
              const rejectionReason = rental.insuranceUuid
                ? rental.insuranceRejectionReason
                : rental.agreementUuid && rental.agreementRejectionReason;
              const period = rentalPeriod(rental);
              const details = [
                rentalTrailerType(rental.trailerType)?.label,
                [rental.make, rental.model, rental.year].filter(Boolean).join(' '),
              ]
                .filter(Boolean)
                .join(', ');
              const documents = [
                { label: 'Insurance Request', url: rental.insurancePdfUrl },
                ...(rental.insuranceAttachments || [])
                  .filter((attachment) => attachment.returnedUrl)
                  .map((attachment) => ({ label: attachment.name, url: attachment.returnedUrl })),
                rental.signedAgreementPdfUrl
                  ? { label: 'Signed Agreement', url: rental.signedAgreementPdfUrl }
                  : { label: 'Agreement', url: rental.agreementPdfUrl },
                {
                  label: 'Outbound Contract',
                  url: rental.outboundContractPdfUrl || (outboundEvent ? rentalAgreementUrl(outboundEvent) : ''),
                },
                { label: 'Return Inspection', url: rental.returnInspectionPdfUrl },
                { label: 'Registration', url: outboundEvent?.trailer?.registrationFile || '' },
                { label: 'Inspection', url: outboundEvent?.trailer?.inspectionFile || '' },
              ].filter((document) => document.url);
              const photos = outboundEvent?.event.photos || [];

              return (
                <Card key={`rental-${rental.id}`} sx={{ p: 2.5 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ sm: 'flex-start' }}
                    gap={2}
                  >
                    <Box>
                      <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                        <Typography variant="h6">
                          {`${isRentalTruckType(rental.trailerType) ? 'Truck' : 'Trailer'} ${rental.trailerUnitNumber}`}
                        </Typography>
                        <CustomerRentalStatusChip rental={rental} outboundEvent={outboundEvent} />
                      </Stack>
                      {details && (
                        <Typography variant="body2" color="text.secondary" pt={0.5}>
                          {details}
                        </Typography>
                      )}
                      {period && (
                        <Typography variant="body2" color="text.secondary">
                          {`Rental Period: ${period}`}
                        </Typography>
                      )}
                    </Box>

                    {action && (
                      <Stack gap={0.5} alignItems={{ xs: 'stretch', sm: 'flex-end' }} flexShrink={0}>
                        <Button
                          variant="contained"
                          color="primary"
                          href={action.href}
                          startIcon={<Iconify icon="solar:pen-bold" width={16} />}
                        >
                          {action.label}
                        </Button>
                        {!!rejectionReason && (
                          <Typography variant="caption" color="error.main" maxWidth={260}>
                            {rejectionReason}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Stack>

                  {(documents.length > 0 || photos.length > 0) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
                        {documents.map((document) => (
                          <DocumentButton key={document.label} label={document.label} url={document.url} />
                        ))}
                        {photos.map((photo) => (
                          <Box key={photo} component="a" href={photo} target="_blank" rel="noopener">
                            <Box
                              component="img"
                              src={photo}
                              sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 0.75, display: 'block' }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}
                </Card>
              );
            })}

            {legacyEvents.map((rental) => {
              const legacyDocuments = [
                { label: 'Rental Agreement', url: rentalAgreementUrl(rental) },
                { label: 'Registration', url: rental.trailer?.registrationFile || '' },
                { label: 'Insurance', url: rental.trailer?.insuranceFile || '' },
                { label: 'Inspection', url: rental.trailer?.inspectionFile || '' },
              ].filter((document) => document.url);
              const photos = rental.event.photos || [];
              const details = [rental.event.make, rental.event.model, rental.event.year]
                .filter(Boolean)
                .join(' ');

              return (
                <Card key={`event-${rental.event.id}`} sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Typography variant="h6">{`Trailer ${rental.event.trailerUnitNumber}`}</Typography>
                    <RentalStatusChip rental={rental} />
                  </Stack>
                  {details && (
                    <Typography variant="body2" color="text.secondary" pt={0.5}>
                      {details}
                    </Typography>
                  )}
                  {rental.event.rentalStartTime && (
                    <Typography variant="body2" color="text.secondary">
                      {`Rental Period: ${DateTime.fromISO(rental.event.rentalStartTime)
                        .setZone(timezone)
                        .toFormat('MM/dd/yyyy')}`}
                    </Typography>
                  )}

                  {(legacyDocuments.length > 0 || photos.length > 0) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
                        {legacyDocuments.map((document) => (
                          <DocumentButton key={document.label} label={document.label} url={document.url} />
                        ))}
                        {photos.map((photo) => (
                          <Box key={photo} component="a" href={photo} target="_blank" rel="noopener">
                            <Box
                              component="img"
                              src={photo}
                              sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 0.75, display: 'block' }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}
                </Card>
              );
            })}
          </Stack>
          </>
        )}
      </DisplayPageData>
    </Container>
  );
}
